import { type FC, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';
import type { BoundingBox, ViewerObjectProps } from '../types';

// Export the variants so, we can use these fabric
// eslint-disable-next-line react-refresh/only-export-components
export enum SegmentVariants {
  LinesAndWireframeMesh,
  LinesAndTransparentMesh,
  LinesOnly,
}
export interface SegmentProps extends ViewerObjectProps {
  lineColor: number;
  lineWidth: number;
  lineOpacity: number;
  fillColor: number;
  fillOpacity: number;
  bounds: BoundingBox;
  label?: string;
  variant?: SegmentVariants;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SegmentExtendedProps extends SegmentProps {
  // boundingBox: THREE.Box3;
}

export const Segment: FC<SegmentExtendedProps> = ({
  lineColor,
  lineWidth,
  lineOpacity,
  fillColor,
  fillOpacity,
  bounds,
  label,
  // If no varient is provided, it will default to transparent
  variant = SegmentVariants.LinesAndTransparentMesh,
}) => {
  const linesGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const indices = new Uint16Array([
      0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7,
    ]);
    const positions = new Float32Array(8 * 3);

    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }, []);

  useEffect(() => {
    return () => {
      linesGeometry.dispose();
    };
  }, [linesGeometry]);

  const { min, max } = bounds;

  useEffect(() => {
    /*
          5____4
        1/___0/|
        | 6__|_7
        2/___3/
        0: max.x, max.y, max.z
        1: min.x, max.y, max.z
        2: min.x, min.y, max.z
        3: max.x, min.y, max.z
        4: max.x, max.y, min.z
        5: min.x, max.y, min.z
        6: min.x, min.y, min.z
        7: max.x, min.y, min.z
    */
    const array = linesGeometry.attributes.position.array as Float32Array;
    array[0] = max[0];
    array[1] = max[1];
    array[2] = max[2];
    array[3] = min[0];
    array[4] = max[1];
    array[5] = max[2];
    array[6] = min[0];
    array[7] = min[1];
    array[8] = max[2];
    array[9] = max[0];
    array[10] = min[1];
    array[11] = max[2];
    array[12] = max[0];
    array[13] = max[1];
    array[14] = min[2];
    array[15] = min[0];
    array[16] = max[1];
    array[17] = min[2];
    array[18] = min[0];
    array[19] = min[1];
    array[20] = min[2];
    array[21] = max[0];
    array[22] = min[1];
    array[23] = min[2];
    linesGeometry.attributes.position.needsUpdate = true;
  }, [linesGeometry, min, max]);

  const boxSize: Parameters<THREE.Vector3['set']> = [
    max[0] - min[0] - 0.0001,
    max[1] - min[1] - 0.0001,
    max[2] - min[2] - 0.0001,
  ];

  const boxPosition: Parameters<THREE.Vector3['set']> = [
    min[0] + boxSize[0] * 0.5,
    min[1] + boxSize[1] * 0.5,
    min[2] + boxSize[2] * 0.5,
  ];

  const zIndexRangeForHtml: number[] = [100, 0];
  const innerHtmlStyle: React.CSSProperties = { pointerEvents: 'none' };
  const divWrappingLabelStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '0',
    borderRadius: '5px',
    minWidth: 60,
    maxWidth: 60,
  };
  const spanWrappingLabelStyle: React.CSSProperties = {
    color: 'black',
    userSelect: 'all',
    fontSize: 10,
  };

  return (
    <group>
      <lineSegments geometry={linesGeometry}>
        <lineBasicMaterial
          attach="material"
          color={lineColor}
          transparent
          opacity={lineOpacity}
          linewidth={lineWidth}
        />
      </lineSegments>
      {variant === SegmentVariants.LinesOnly ? null : (
        <mesh scale={boxSize} position={boxPosition}>
          {label && (
            <Html center as="div" zIndexRange={zIndexRangeForHtml} style={innerHtmlStyle}>
              <div style={divWrappingLabelStyle}>
                <span style={spanWrappingLabelStyle}>{label}</span>
              </div>
            </Html>
          )}
          <boxGeometry attach="geometry" />
          <meshBasicMaterial
            attach="material"
            color={fillColor}
            wireframe={variant === SegmentVariants.LinesAndWireframeMesh}
            transparent={variant === SegmentVariants.LinesAndTransparentMesh}
            opacity={fillOpacity}
          />
        </mesh>
      )}
    </group>
  );
};
